import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, inject, Input, output, viewChildren } from '@angular/core'
import { UntilDestroy } from '@ngneat/until-destroy'

import { PopupService } from '@app/modules/popup/popup.service'
import { UIModule } from '@app/modules/ui/ui.module'
import { MemberService } from '@app/services/member.service'

import type { Conversation } from '../../models/conversation.model'
import type { Message } from '../../models/message.model'
import { ConversationUIService } from '../../services/conversation-ui.service'
import { MessageComponent } from '../message/message.component'
import { MessageTypingComponent } from '../message-typing/message-typing.component'
import { ConversationStore } from '../store/conversation.store'

@UntilDestroy()
@Component({
  selector: 'sb-messages',
  templateUrl: './messages.component.html',
  styleUrls: ['./messages.component.scss'],
  imports: [CommonModule, UIModule, MessageComponent, MessageTypingComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MessagesComponent {
  readonly conversationStore = inject(ConversationStore)
  private readonly popupService = inject(PopupService)
  private readonly conversationUIService = inject(ConversationUIService)
  readonly memberService = inject(MemberService)

  @Input({ required: true }) conversation!: Conversation

  readonly messageWithBoostToggle = computed(() => {
    if (this.memberService.account.dependent) {
      return undefined
    }
    for (const message of [...this.conversationStore.messages()].reverse()) {
      if (!message.is_me || message.boosted || message.boostInProgress) {
        return undefined
      }
      if (
        message.type === 'text' ||
        ((message.type === 'photo' || message.type === 'voice') && message.status === 'viewable')
      ) {
        return message
      }
    }
    return undefined
  })

  readonly messageWithReadReceipts = computed(() => {
    for (const message of [...this.conversationStore.messages()].reverse()) {
      if (!message.is_me) {
        return undefined
      }
      if (
        message.type === 'text' ||
        ((message.type === 'photo' || message.type === 'voice') && message.status !== 'pending')
      ) {
        return message
      }
    }
    return undefined
  })

  readonly messageComponents = viewChildren(MessageComponent)
  readonly messageChanged = output<void>()

  onShowReadReceiptsClick(message: Message): void {
    if (!this.popupService.getPopupRequest('message-read-receipts-intro')) {
      this.conversationStore.attemptToUnlockReadReceipts(message, this.conversation, 'default')
      return
    }

    const instance = this.conversationUIService.openUnlockReadReceiptsIntroModal()
    instance.onConfirm(() => this.conversationStore.attemptToUnlockReadReceipts(message, this.conversation, 'default'))
  }
}
